'use client'
import { useRef, type RefObject } from 'react'
import { motion, useScroll, useTransform, useReducedMotion, type MotionValue } from 'framer-motion'

/**
 * Why-MRN chapter diagrams — clean line/diagram work in the brand palette.
 *
 * Extracted from WhyJourney so the section file stays focused on the scroll
 * timeline + layout. Each visual draws on a `draw` MotionValue (the chapter's
 * hold window in the desktop filmstrip) or its own in-view scroll when
 * `stacked` (mobile / reduced-motion). See ./useWhyTimeline for how `draw` is
 * produced, and VISUAL_CSS for the sizing + RTL mirror these rely on.
 */

// Brand tokens — SVG fills/strokes accept CSS custom properties.
const C = {
  bg: 'var(--color-surface)',
  ink: 'var(--color-ink)',
  inkMuted: 'var(--color-ink-muted)',
  line: 'var(--color-line)',
  navy: 'var(--color-blue)',
  cold: 'var(--color-blue-soft)',
  live: 'var(--color-action)',
  accent: 'var(--color-accent)',
}

// Localise numerals so "4.2" reads ۴.۲ / ٤.٢ in fa / ar.
const DIGITS: Record<string, string> = {
  fa: '۰۱۲۳۴۵۶۷۸۹',
  ar: '٠١٢٣٤٥٦٧٨٩',
}
export const loc = (s: string, lang: string) =>
  DIGITS[lang] ? s.replace(/[0-9]/g, (d) => DIGITS[lang][+d]) : s

export type VisualKind = 'setup' | 'source' | 'stock' | 'route' | 'chain'
// Visuals map to t.why.chapters by order.
export const VISUALS: VisualKind[] = ['setup', 'source', 'stock', 'route', 'chain']

// ── chapter visuals (clean line / diagram work, brand palette) ───────────────

const SVG = 'wj-vis'

/** Many channels, one direct line — the lines extend toward the dots on scroll.
 *  Desktop filmstrip: tied to the SECTION scroll, so the draw STARTS once the
 *  chapter is centred (progress 0) and completes as continued scrolling pans it
 *  onward. Mobile stacked: a per-element scroll draws it as the SVG enters view
 *  (the section scroll isn't driven there). The navy line reaches the red dot. */
function VisualSetup({ draw, stacked }: { draw: MotionValue<number>; stacked: boolean }) {
  const ref = useRef<SVGSVGElement>(null)
  const reduce = useReducedMotion()
  // Mobile (no filmstrip hold): draw as the SVG scrolls up into view.
  const own = useScroll({
    target: ref as unknown as RefObject<HTMLElement>,
    offset: ['start end', 'center center'],
  }).scrollYProgress
  // Desktop: `draw` is the chapter's hold window — fills while pinned, before the pan.
  const grow = reduce ? 1 : stacked ? own : draw
  const channels = [
    'M44 190 C200 70 320 120 516 78',
    'M44 190 C180 150 300 300 516 150',
    'M44 190 C220 260 340 210 516 304',
    'M44 190 C150 230 360 360 516 232',
  ]
  return (
    <svg ref={ref} viewBox="0 0 560 380" className={SVG} aria-hidden fill="none">
      <g stroke={C.ink} opacity="0.16" strokeWidth="2">
        {channels.map((d) => (
          <motion.path key={d} d={d} style={{ pathLength: grow }} />
        ))}
      </g>
      {[78, 150, 232, 304].map((y) => (
        <circle key={y} cx="516" cy={y} r="5" fill={C.ink} opacity="0.22" />
      ))}
      <motion.path
        d="M44 190 L516 190"
        stroke={C.navy}
        strokeWidth="4"
        style={{ pathLength: grow }}
      />
      <circle cx="44" cy="190" r="8" fill={C.navy} />
      <circle cx="516" cy="190" r="11" fill={C.live} stroke={C.bg} strokeWidth="3" />
    </svg>
  )
}

/** MRN as the first-hand hub: manufacturer → MRN (one verified link) → the
 *  shops, with the long many-handed re-export chain faded underneath. On scroll
 *  the source→MRN link forms first, the seal stamps, then MRN's spokes reach the
 *  shops — staged across the chapter's hold window. */
function VisualSource({ draw, stacked }: { draw: MotionValue<number>; stacked: boolean }) {
  const ref = useRef<SVGSVGElement>(null)
  const reduce = useReducedMotion()
  const own = useScroll({
    target: ref as unknown as RefObject<HTMLElement>,
    offset: ['start end', 'center center'],
  }).scrollYProgress
  const base = stacked ? own : draw
  // Stage the build across the hold: link (→MRN) first, seal stamps, then spokes.
  const linkMV = useTransform(base, [0, 0.5], [0, 1])
  const sealMV = useTransform(base, [0.46, 0.62], [0, 1])
  const spokeMV = useTransform(base, [0.55, 1], [0, 1])
  const link = reduce ? 1 : linkMV
  const seal = reduce ? 1 : sealMV
  const spoke = reduce ? 1 : spokeMV
  const shops = [
    [470, 98],
    [496, 180],
    [470, 262],
  ] as const
  return (
    <svg ref={ref} viewBox="0 0 560 360" className={SVG} aria-hidden fill="none">
      {/* the long way — product through many hands (faded alternative) */}
      <g opacity="0.2" stroke={C.ink}>
        <path d="M70 326 H500" strokeWidth="2" strokeDasharray="2 9" />
        {[70, 156, 242, 328, 414, 500].map((x) => (
          <circle key={x} cx={x} cy="326" r="6" fill="none" strokeWidth="2" />
        ))}
      </g>

      {/* manufacturer (the source) — building body centred on the y=180 axis so
          the outgoing link exits its middle, not its roof */}
      <g transform="translate(50 109)">
        <path d="M4 92 V44 L32 56 V44 L60 56 V44 L88 56 V92 Z" fill={C.navy} />
        <rect x="2" y="90" width="90" height="8" rx="2" fill={C.navy} />
        <rect x="66" y="14" width="13" height="34" rx="1" fill={C.navy} />
        <rect x="16" y="66" width="12" height="13" fill={C.bg} />
        <rect x="40" y="66" width="12" height="13" fill={C.bg} />
        <rect x="64" y="66" width="12" height="13" fill={C.bg} />
      </g>

      {/* first-hand link: source → MRN draws toward the hub, then the seal stamps */}
      <motion.line
        x1="144"
        y1="180"
        x2="234"
        y2="180"
        stroke={C.navy}
        strokeWidth="5"
        style={{ pathLength: link }}
      />
      <motion.g style={{ opacity: seal }}>
        <circle cx="189" cy="180" r="11" fill={C.bg} stroke={C.live} strokeWidth="4" />
        {/* wj-keepup counter-flips the tick under RTL so it doesn't mirror into
            a reversed checkmark (the circle is symmetric, so it needs nothing). */}
        <path
          className="wj-keepup"
          d="M183.5 180 l4 5 l8.5 -10.5"
          stroke={C.live}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </motion.g>

      {/* spokes: MRN → shops — reach out once the link is made */}
      {shops.map(([sx, sy], i) => (
        <motion.line
          key={`spoke-${i}`}
          x1="322"
          y1="180"
          x2={sx - 4}
          y2={sy}
          stroke={C.navy}
          strokeWidth="3"
          style={{ pathLength: spoke }}
        />
      ))}

      {/* MRN hub */}
      <circle cx="280" cy="180" r="42" fill="none" stroke={C.live} strokeWidth="5" />
      <circle cx="280" cy="180" r="33" fill={C.navy} />
      <g stroke={C.bg} strokeWidth="3" strokeLinecap="round">
        <path d="M280 180 L280 161 M280 180 L296 191 M280 180 L264 191" />
      </g>
      {[
        [280, 161],
        [296, 191],
        [264, 191],
      ].map(([cx, cy]) => (
        <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="3.6" fill={C.bg} />
      ))}
      <text x="280" y="248" textAnchor="middle" className="wj-hub wj-keepup" fill={C.navy}>
        MRN
      </text>

      {/* shops */}
      {shops.map(([sx, sy], i) => (
        <g key={`shop-${i}`} transform={`translate(${sx} ${sy - 30})`}>
          <path d="M0 18 L7 4 L53 4 L60 18 Z" fill={C.navy} />
          <rect x="4" y="18" width="52" height="42" fill={C.navy} />
          <rect x="22" y="34" width="16" height="26" fill={C.bg} />
          <rect x="10" y="27" width="9" height="11" fill={C.bg} />
          <rect x="41" y="27" width="9" height="11" fill={C.bg} />
        </g>
      ))}
    </svg>
  )
}

/** Shelf-life runway. Each track is the product's full life, made → expiry. The
 *  faded stretch is life used up before it reached you; the solid stretch is
 *  life left for you. A vial marks where the stock lands on that runway: MRN
 *  arrives early (a long navy runway ahead), re-export arrives late (a crimson
 *  sliver jammed against the expiry wall). No numbers — the contrast is the point. */
function VisualStock({ draw, stacked }: { draw: MotionValue<number>; stacked: boolean }) {
  const ref = useRef<SVGSVGElement>(null)
  const reduce = useReducedMotion()
  const own = useScroll({
    target: ref as unknown as RefObject<HTMLElement>,
    offset: ['start end', 'center center'],
  }).scrollYProgress
  const base = stacked ? own : draw
  // Both runways START FULL (full shelf life) and begin aging together. The
  // used-up life washes in from the made-date (left) and each vial settles where
  // its stock lands: MRN early (lots of life left), re-export runs further/longer
  // to a sliver near the expiry wall.
  const p0 = useTransform(base, [0, 0.6], [0, 1])
  const p1 = useTransform(base, [0, 1], [0, 1])
  const uw0 = useTransform(p0, [0, 1], [0, 82])
  const vx0 = useTransform(p0, [0, 1], [70, 152])
  const uw1 = useTransform(p1, [0, 1], [0, 362])
  const vx1 = useTransform(p1, [0, 1], [70, 432])
  const rows = [
    {
      y: 110,
      arrive: 152,
      remain: C.navy,
      baseOp: 1,
      uw: reduce ? 82 : uw0,
      vx: reduce ? 152 : vx0,
    },
    {
      y: 224,
      arrive: 432,
      remain: C.live,
      baseOp: 0.85,
      uw: reduce ? 362 : uw1,
      vx: reduce ? 432 : vx1,
    },
  ]
  return (
    <svg ref={ref} viewBox="0 0 560 320" className={SVG} aria-hidden fill="none">
      <defs>
        {rows.map((r) => (
          <clipPath key={r.y} id={`wj-track-${r.y}`}>
            <rect x="70" y={r.y} width="430" height="40" rx="8" />
          </clipPath>
        ))}
      </defs>

      {/* calendar-check — "long-dated, verified" — over the good runway.
          Position via an OUTER group; keep the wj-keepup counter-flip on an
          INNER group. (CSS transform from wj-keepup overrides an SVG transform
          attribute, so combining them on one element drops the translate under
          RTL and the icon jumps.) */}
      <g transform="translate(284 38)">
        <g className="wj-keepup">
          <line
            x1="10"
            y1="-7"
            x2="10"
            y2="5"
            stroke={C.navy}
            strokeWidth="3"
            strokeLinecap="round"
          />
          <line
            x1="30"
            y1="-7"
            x2="30"
            y2="5"
            stroke={C.navy}
            strokeWidth="3"
            strokeLinecap="round"
          />
          <rect
            x="0"
            y="0"
            width="40"
            height="38"
            rx="6"
            fill={C.bg}
            stroke={C.navy}
            strokeWidth="2.5"
          />
          <line x1="0" y1="13" x2="40" y2="13" stroke={C.navy} strokeWidth="2.5" />
          <path
            d="M10 25 l6 6 l14 -15"
            fill="none"
            stroke={C.navy}
            strokeWidth="3.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </g>
      </g>

      {rows.map((r) => (
        <g key={r.y}>
          <g clipPath={`url(#wj-track-${r.y})`}>
            {/* full shelf life — present from the start */}
            <rect x="70" y={r.y} width="430" height="40" fill={r.remain} opacity={r.baseOp} />
            {/* life used up before it reached you — washes in from the made-date
                (left) as the vial advances to where the stock lands */}
            <g transform={`translate(70 ${r.y})`}>
              <motion.rect height="40" fill={C.bg} opacity="0.8" style={{ width: r.uw }} />
            </g>
          </g>
          <rect
            x="70"
            y={r.y}
            width="430"
            height="40"
            rx="8"
            fill="none"
            stroke={C.line}
            strokeWidth="2"
          />
          {/* expiry wall — the deadline at the end of every runway, in the
              runway's own colour. The MRN vial lands far from it; the re-export
              vial jams against it. */}
          <rect x="495" y={r.y - 6} width="6" height="52" rx="2" fill={r.remain} />
          {/* arrival vial — travels to where the stock lands */}
          <motion.g style={{ x: r.vx, y: r.y - 46 }}>
            <line x1="0" y1="33" x2="0" y2="46" stroke={C.navy} strokeWidth="2" />
            <rect x="-5" y="0" width="10" height="6" rx="1.5" fill={C.navy} />
            <path
              d="M-7 8 h14 v17 a7 7 0 0 1 -14 0 z"
              fill={C.bg}
              stroke={C.navy}
              strokeWidth="2.5"
            />
            <path d="M-7 18 h14 v7 a7 7 0 0 1 -14 0 z" fill={C.navy} opacity="0.2" />
          </motion.g>
        </g>
      ))}
    </svg>
  )
}

/** "Direct vs. the detour." Two shipments leave Tehran for the same market: one
 *  takes the short direct route and arrives fast; the other crawls through a long
 *  chain of intermediaries. Speed (and the cost of layers) is shown by the race. */
function VisualRoute({ draw, stacked }: { draw: MotionValue<number>; stacked: boolean }) {
  const ref = useRef<SVGSVGElement>(null)
  const reduce = useReducedMotion()
  const own = useScroll({
    target: ref as unknown as RefObject<HTMLElement>,
    offset: ['start end', 'center center'],
  }).scrollYProgress
  const base = stacked ? own : draw

  // Direct route — a gentle arc, Tehran → market (arrives at x≈450).
  const q = (a: number, c: number, b: number, t: number) => {
    const v = 1 - t
    return v * v * a + 2 * v * t * c + t * t * b
  }
  const dirX = (t: number) => q(70, 270, 450, t)
  const dirY = (t: number) => q(180, 120, 180, t)

  // Detour — long, winding through intermediaries to the same market.
  const W = [
    [70, 180],
    [150, 252],
    [236, 300],
    [326, 252],
    [412, 300],
    [450, 180],
  ]
  const segs = W.slice(1).map((p, i) => ({
    ax: W[i][0],
    ay: W[i][1],
    dx: p[0] - W[i][0],
    dy: p[1] - W[i][1],
    len: Math.hypot(p[0] - W[i][0], p[1] - W[i][1]),
  }))
  const total = segs.reduce((s, x) => s + x.len, 0)
  const det = (t: number, axis: 0 | 1) => {
    let d = t * total
    for (const s of segs) {
      if (d <= s.len) {
        const f = s.len ? d / s.len : 0
        return axis === 0 ? s.ax + s.dx * f : s.ay + s.dy * f
      }
      d -= s.len
    }
    return axis === 0 ? 450 : 180
  }
  const detourD = `M${W.map((p) => p.join(' ')).join(' L')}`

  // The direct shipment arrives well before the detour one even gets close.
  const fastT = useTransform(base, [0, 0.4], [0, 1])
  const slowT = useTransform(base, [0, 1], [0, 1])
  const fxMV = useTransform(fastT, (t) => dirX(t))
  const fyMV = useTransform(fastT, (t) => dirY(t))
  const sxMV = useTransform(slowT, (t) => det(t, 0))
  const syMV = useTransform(slowT, (t) => det(t, 1))
  const fX = reduce ? 450 : fxMV
  const fY = reduce ? 180 : fyMV
  const sX = reduce ? 450 : sxMV
  const sY = reduce ? 180 : syMV

  return (
    <svg ref={ref} viewBox="0 0 560 360" className={SVG} aria-hidden fill="none">
      {/* detour — the long way through many intermediaries (faded) */}
      <path d={detourD} stroke={C.ink} strokeWidth="2" strokeDasharray="2 9" opacity="0.28" />
      {W.slice(1, -1).map(([x, y]) => (
        <circle
          key={`n${x}`}
          cx={x}
          cy={y}
          r="6"
          fill="none"
          stroke={C.ink}
          strokeWidth="2"
          opacity="0.3"
        />
      ))}

      {/* direct — the short fast lane */}
      <path d="M70 180 Q270 120 450 180" stroke={C.navy} strokeWidth="2.5" opacity="0.5" />

      {/* Tehran origin */}
      <circle cx="70" cy="180" r="19" fill={C.navy} />
      <circle cx="70" cy="180" r="19" fill="none" stroke={C.live} strokeWidth="5" />
      <circle cx="70" cy="180" r="6" fill={C.bg} />

      {/* destination market */}
      <g transform="translate(465 150)">
        <path d="M0 18 L7 4 L53 4 L60 18 Z" fill={C.navy} />
        <rect x="4" y="18" width="52" height="42" fill={C.navy} />
        <rect x="22" y="34" width="16" height="26" fill={C.bg} />
        <rect x="10" y="27" width="9" height="11" fill={C.bg} />
        <rect x="41" y="27" width="9" height="11" fill={C.bg} />
      </g>

      {/* slow shipment crawling the detour */}
      <motion.g style={{ x: sX, y: sY }}>
        <rect x="-9" y="-7" width="18" height="14" rx="1.5" fill={C.ink} opacity="0.5" />
        <line x1="-9" y1="-2" x2="9" y2="-2" stroke={C.bg} strokeWidth="1.5" opacity="0.7" />
      </motion.g>

      {/* fast shipment on the direct lane */}
      <motion.g style={{ x: fX, y: fY }}>
        <rect x="-10" y="-8" width="20" height="16" rx="2" fill={C.navy} />
        <line x1="-10" y1="-2" x2="10" y2="-2" stroke={C.bg} strokeWidth="2" />
      </motion.g>
    </svg>
  )
}

/** TEMP (revert this week): the cold-chain instrument (temperature trace +
 *  snowflake + live °C readout) is replaced with a neutral quality seal —
 *  a shield + check that draws on scroll — for the new "quality control &
 *  support" chapter. Original is in git history (this same function). */
function VisualChain({ draw }: { draw: MotionValue<number>; temp: string }) {
  return (
    <svg viewBox="0 0 560 340" className={SVG} aria-hidden fill="none">
      {/* faint shield ghost */}
      <path
        d="M280 64 L372 104 L372 178 C372 236 332 268 280 286 C228 268 188 236 188 178 L188 104 Z"
        stroke={C.navy}
        strokeWidth="2"
        strokeLinejoin="round"
        opacity="0.16"
      />
      {/* shield draws on scroll */}
      <motion.path
        d="M280 64 L372 104 L372 178 C372 236 332 268 280 286 C228 268 188 236 188 178 L188 104 Z"
        stroke={C.navy}
        strokeWidth="3"
        strokeLinejoin="round"
        style={{ pathLength: draw }}
      />
      {/* verified check draws on scroll */}
      <motion.path
        d="M242 176 L272 206 L330 142"
        stroke={C.live}
        strokeWidth="5"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ pathLength: draw }}
      />
    </svg>
  )
}

export function Visual({
  kind,
  draw,
  temp,
  stacked,
}: {
  kind: VisualKind
  draw: MotionValue<number>
  temp: string
  stacked: boolean
}) {
  if (kind === 'setup') return <VisualSetup draw={draw} stacked={stacked} />
  if (kind === 'source') return <VisualSource draw={draw} stacked={stacked} />
  if (kind === 'stock') return <VisualStock draw={draw} stacked={stacked} />
  if (kind === 'route') return <VisualRoute draw={draw} stacked={stacked} />
  return <VisualChain draw={draw} temp={temp} />
}

// Shared SVG-visual styling. MUST be rendered in BOTH the stacked and filmstrip
// branches: the diagrams carry `class="wj-vis"` and rely on this rule for their
// width. Without it the inline <svg> has no intrinsic size and iOS Safari
// collapses it to zero inside the flex column (Chrome falls back to a default
// size, which is why it only broke on iOS). Includes the SVG <text> fonts and
// the RTL mirror/counter-flip so those work in the stacked layout too.
export const VISUAL_CSS = `
  /* diagrams stay LTR so labels + the temperature readout never mirror */
  .wj-vis { width: min(680px, 100%); height: auto; direction: ltr; }
  .wj-vsmall { font-family: var(--font-sans, system-ui); font-size: 15px; font-weight: 600; letter-spacing: 0.16em; }
  .wj-readout { font-family: var(--font-display-serif, Georgia, serif); font-size: 40px; font-weight: 700; letter-spacing: -0.5px; }
  .wj-hub { font-family: var(--font-display-serif, Georgia, serif); font-size: 26px; font-weight: 700; letter-spacing: 1px; }
  /* RTL: mirror the directional diagrams so they point the reading way, then
     counter-flip any text (readout, MRN mark) so it stays upright. */
  html[dir="rtl"] .wj-vis { transform: scaleX(-1); }
  html[dir="rtl"] .wj-readout,
  html[dir="rtl"] .wj-keepup { transform: scaleX(-1); transform-box: fill-box; transform-origin: center; }
`
